import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useMemo } from "react";
import { GenericConfigContext } from "/contexts/generic-config-context";
import { getCurrencyName, getCurrencyWithPriceDisplay, getCurrencySign, shouldShowCurrencyText } from "/services/pricing-service";
import { getFreeTierSeatsCountByAbTest, shouldSeeCrmPricingIncreaseTest } from "services/ab-tests/monetization-ab-tests-service";
import { TIERS } from "constants/pricing";
import { calculateFixedPricing, getPricingDisplayConfig } from "services/pricing/fixed-pricing-service";
import { handleRuntimeError } from "client/services/exceptions-handling-service/exceptions-handling-service";
import { StyledProductPrice } from "./product-price.scss";
import { PRICING_TYPES } from "segments/desktop/pricing/constants";
import { VERSION_16 } from "constants/pricing-versions";
export const ProductPrice = ({ period, seats, tierName, product, hideFreeForever, splitCurrencySign, isNewPricing, unitOfMeasure })=>{
    var _pricingResult_totalPricePerMonth, _pricingResult_totalPrice;
    const { translate, translateOrTranslateWithParam, currency: currencyFromContext, pricingVersion, localeId, forceCurrency, cloudfrontViewerCountry, cookies, persistantCookies, forcePricing, abTests, simulateCrmPricingIncreaseTest } = useContext(GenericConfigContext);
    const isCRMPriceIncreaseTest = shouldSeeCrmPricingIncreaseTest(persistantCookies, abTests, simulateCrmPricingIncreaseTest);
    const currency = currencyFromContext || getCurrencyName({
        forceCurrency,
        countryCode: cloudfrontViewerCountry
    });
    const pricingResult = useMemo(()=>calculateFixedPricing({
            product,
            pricingVersion: isCRMPriceIncreaseTest ? VERSION_16 : pricingVersion,
            period,
            tierName,
            currency,
            unitOfMeasure,
            quantity: seats
        }), [
        product,
        pricingVersion,
        period,
        tierName,
        currency,
        unitOfMeasure,
        seats
    ]);
    const displayConfig = useMemo(()=>getPricingDisplayConfig(pricingResult, unitOfMeasure), [
        pricingResult,
        unitOfMeasure
    ]);
    if (!pricingResult.isValid) {
        const params = {
            product,
            period,
            currency,
            tierName,
            seats,
            unitOfMeasure
        };
        handleRuntimeError(`ProductPrice: error calculating pricing. Params: ${JSON.stringify(params)}`);
    }
    const pricePerSeat = getCurrencyWithPriceDisplay({
        price: (_pricingResult_totalPricePerMonth = pricingResult.totalPricePerMonth) === null || _pricingResult_totalPricePerMonth === void 0 ? void 0 : _pricingResult_totalPricePerMonth.toLocaleString(),
        currency,
        localeId,
        hideCurrency: splitCurrencySign
    });
    const totalPrice = getCurrencyWithPriceDisplay({
        price: pricingResult === null || pricingResult === void 0 ? void 0 : (_pricingResult_totalPrice = pricingResult.totalPrice) === null || _pricingResult_totalPrice === void 0 ? void 0 : _pricingResult_totalPrice.toLocaleString(),
        currency,
        localeId,
        className: "product-price-total",
        unitOfMeasure
    });
    const currencySignDisplay = useMemo(()=>getCurrencySign(currency), [
        currency
    ]);
    const renderUpToSeats = ()=>{
        return /*#__PURE__*/ _jsx("div", {
            className: "product-price-total",
            children: translateOrTranslateWithParam({
                key: "pricingPage.upToXSeats",
                param: getFreeTierSeatsCountByAbTest({
                    cookies,
                    abTests,
                    forcePricing
                })
            })
        });
    };
    const renderPricePerMonth = ()=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "product-price-total-wrapper",
                    children: [
                        displayConfig.showTotalLabel && /*#__PURE__*/ _jsx("span", {
                            className: "product-price-total",
                            children: translate("pricingPage.total")
                        }),
                        displayConfig.pricingType === PRICING_TYPES.FIXED ? /*#__PURE__*/ _jsxs("div", {
                            className: "price-with-unit",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: `product-price-total contacts-pricing-unit`,
                                    children: totalPrice
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: `product-price-total contacts-text-unit`,
                                    children: translate("pricingPage.perMonthSlash")
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "product-price-total",
                                    children: totalPrice
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "product-price-total",
                                    children: translate("pricingPage.perMonthSlash")
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "product-price-billed-periodically",
                    children: translate(`pricingPage.periods.${period}.billed`)
                })
            ]
        });
    };
    const renderFreeForever = ()=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                translate("pricingPage.free"),
                /*#__PURE__*/ _jsx("br", {}),
                " ",
                translate("pricingPage.forever")
            ]
        });
    };
    const renderSeatPerMonth = ()=>{
        return /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                translate("pricingPage.seatOver"),
                /*#__PURE__*/ _jsx("br", {}),
                " ",
                translate("pricingPage.month")
            ]
        });
    };
    const renderDiscountChip = ()=>{
        return /*#__PURE__*/ _jsx("div", {
            className: "discount-chip",
            children: translateOrTranslateWithParam({
                key: "pricingPage.percentageOff",
                param: 18
            })
        });
    };
    const renderNewPriceDescription = ()=>{
        return /*#__PURE__*/ _jsxs("div", {
            className: "price-description-new",
            children: [
                translate("pricingPage.perSeatPerMonth"),
                ", ",
                period === "monthly" ? translate("pricingPage.billedMonthly") : translate("pricingPage.billedAnnually")
            ]
        });
    };
    const shouldSeeFreeForever = useMemo(()=>{
        return tierName === TIERS.FREE && !hideFreeForever;
    }, [
        hideFreeForever,
        tierName
    ]);
    return /*#__PURE__*/ _jsxs(StyledProductPrice, {
        className: "price",
        isNewPricing: isNewPricing,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "price-tag",
                children: [
                    splitCurrencySign && displayConfig.showCurrencySign && /*#__PURE__*/ _jsxs("div", {
                        className: `currency-sign ${isNewPricing ? "bold" : ""}`,
                        children: [
                            currencySignDisplay,
                            shouldShowCurrencyText(currency) && /*#__PURE__*/ _jsx("div", {
                                className: "price-currency-text",
                                children: currency.toUpperCase()
                            })
                        ]
                    }),
                    displayConfig.showAmount && /*#__PURE__*/ _jsx("div", {
                        className: "amount",
                        children: pricePerSeat
                    }),
                    displayConfig.showPriceDescription && /*#__PURE__*/ _jsxs("div", {
                        className: "price-description",
                        children: [
                            shouldSeeFreeForever && renderFreeForever(),
                            !shouldSeeFreeForever && !isNewPricing && renderSeatPerMonth(),
                            isNewPricing && period === "yearly" && renderDiscountChip()
                        ]
                    })
                ]
            }),
            tierName === TIERS.FREE && !isNewPricing && renderUpToSeats(),
            tierName !== TIERS.FREE && !isNewPricing && renderPricePerMonth(),
            isNewPricing && renderNewPriceDescription(),
            /*#__PURE__*/ _jsx("div", {})
        ]
    });
};
